import { useState, useEffect, useRef } from 'react'
import {
    Box, Button, Modal, Stack, Card, CardContent, Typography,
    TextField, Snackbar, Alert, MenuItem, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material'
import moment from 'moment'
import 'moment/locale/th'

// Hooks & Constants
import useTrackDoc from './hooks/useTrackDoc'
import useAuth from '../../hooks/useAuth'
import { PARCEL_TYPE_OPTIONS } from '../../constants/parcelConstants'

// ICON
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'

const cardBodyStyle = {
    border: 'grey',
    background: '#fafaeb',
    textAlign: 'center',
    m: 2,
    width: '90%',
    justifyContent: 'center',
}

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
};

const initialForm = {
    parcelCode: '',
    oldParcel: '',
    parcelType: '1',
    dn: ''
};

const initialFormError = {
    parcelCode: '',
    oldParcel: '',
    parcelType: '',
    dn: ''
};

// 🟢 ID จำลองสำหรับเสนอผู้บริหาร
const MANAGEMENT_HOLDER_ID = -1;

export default function ReceiveDocuments() {
    const [docData, setDocData] = useState([])
    const [barcode, setBarcode] = useState('')
    const inputRef = useRef(null);

    // 🟢 ตรวจสอบ Role ของผู้ใช้งานปัจจุบัน
    const { user } = useAuth()
    const ALLOWED_RETURN_ROLES = ['100', '105', 100, 105]; // Admin = 100, เจ้าหน้าที่ห้องซอง = 105
    const canReturnDoc = ALLOWED_RETURN_ROLES.includes(user?.roles);

    // 🟢 สิทธิ์สำหรับปุ่มเสนอผู้บริหาร (Admin = 100, เจ้าหน้าที่ธุรการ = 106)
    const ALLOWED_ADMIN_DOC_ROLES = ['100', '106', 100, 106];
    const canSendToManagement = ALLOWED_ADMIN_DOC_ROLES.includes(user?.roles);


    // Hooks
    const { fetchAndAppendFolderData, updateCurrentHolder, returnDoc, addFolder } = useTrackDoc()

    const handleBarcodeChange = (e) => {
        setBarcode(e.target.value.toUpperCase())
    };

    const handleBarcodeSubmit = (e) => {
        e.preventDefault()
        if (!barcode.trim()) return;

        const found = docData.some(item => item.parcelCode === barcode);
        if (found) {
            console.log("พบ parcelCode ที่ตรงกับ Barcode ในตารางแล้ว");
        } else {
            fetchAndAppendFolderData(barcode, docData, setDocData, setOpenError, setErrorText)
        }
        setBarcode('')
    };

    // Alert
    const [openSuccess, setOpenSuccess] = useState(false);
    const [successText, setSuccessText] = useState('')
    const [openError, setOpenError] = useState(false)
    const [errorText, setErrorText] = useState('')

    const handleSuccessAlertClose = () => {
        setOpenSuccess(false);
        setSuccessText('')
        setDocData([])
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleErrorAlertClose = () => {
        setOpenError(false);
        setErrorText('')
    };

    // Add Folder Modal
    const [addModalOpen, setAddModalOpen] = useState(false)
    const [modalForm, setModalForm] = useState(initialForm);
    const [validationErrors, setValidationErrors] = useState({});

    const handleModalClose = () => {
        setAddModalOpen(false)
        setModalForm(initialForm)
        setValidationErrors(initialFormError)
    };

    const handleModalFormChange = (e) => {
        setModalForm({ ...modalForm, [e.target.name]: e.target.value.toUpperCase() });
        setValidationErrors({ ...validationErrors, [e.target.name]: null });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!modalForm.parcelCode) newErrors.parcelCode = 'กรุณากรอกรหัสแปลงที่ดิน';
        if (!modalForm.oldParcel) newErrors.oldParcel = 'กรุณากรอกรหัสแปลงที่ดินเก่า';
        if (!modalForm.parcelType) newErrors.parcelType = 'กรุณาเลือกประเภทเอกสารสิทธิ์';
        if (!modalForm.dn) newErrors.dn = 'กรุณากรอกเลขเอกสารสิทธิ์ที่ดิน';
        setValidationErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleModalFormSave = (e) => {
        e.preventDefault()
        if (!validateForm()) return;
        addFolder(modalForm, setOpenSuccess, setOpenError, setSuccessText, setErrorText)
        handleModalClose();
    };

    // 🟢 ฟังก์ชันส่งเสนอผู้บริหาร
    const handleSendToManagement = () => {
        if (docData.length === 0) {
            setErrorText('ไม่มีรายการเอกสารในตาราง');
            setOpenError(true);
            return;
        }
        // ส่ง MANAGEMENT_HOLDER_ID (-1) ไปให้ API
        updateCurrentHolder(docData, setOpenSuccess, setOpenError, setSuccessText, setErrorText, MANAGEMENT_HOLDER_ID);
    };

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [addModalOpen])

    return (
        <Box className='otherSide' id="otherSide">
            <Box className='surveyLayouts' id="surveyLayouts">
                {/* Alert แจ้งเตือน */}
                <Snackbar
                    open={openSuccess}
                    autoHideDuration={2000}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    onClose={handleSuccessAlertClose}
                >
                    <Alert variant='filled' severity="success" sx={{ width: '100%' }}>
                        {successText}
                    </Alert>
                </Snackbar>

                <Snackbar
                    open={openError}
                    autoHideDuration={3000}
                    onClose={handleErrorAlertClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert variant="filled" severity="error" sx={{ width: '100%' }}>
                        {errorText}
                    </Alert>
                </Snackbar>

                <Card sx={cardBodyStyle}>
                    <CardContent>
                        <Typography variant='h5' sx={{ fontWeight: 'bold' }}>ตารางรับเอกสาร</Typography>
                    </CardContent>

                    <CardContent>
                        {/* ช่องสแกน Barcode */}
                        <Box mb={2} component="form" onSubmit={handleBarcodeSubmit}>
                            <Typography variant="h6" gutterBottom>
                                สแกน Barcode เพื่อเพิ่มเอกสาร
                            </Typography>
                            <TextField
                                inputRef={inputRef}
                                label="Scan or Enter Barcode"
                                variant="outlined"
                                value={barcode}
                                onChange={handleBarcodeChange}
                            />
                            <Button
                                variant="contained"
                                color="secondary"
                                type='submit'
                                startIcon={<DocumentScannerIcon />}
                                sx={{ fontSize: '1.2rem', padding: '12px 24px', ml: 1 }}
                            >
                                สแกน
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<AddCircleIcon />}
                                sx={{ fontSize: '1.2rem', padding: '12px 24px', ml: 1 }}
                                onClick={() => setAddModalOpen(true)}
                            >
                                เพิ่มซองในระบบ
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<ClearIcon />}
                                sx={{ fontSize: '1.2rem', padding: '12px 24px', ml: 1 }}
                                onClick={() => {
                                    setDocData([])
                                    if (inputRef.current) inputRef.current.focus();
                                }}
                            >
                                เอาออกทั้งหมด
                            </Button>
                        </Box>

                        {/* ตารางแสดงรายการเอกสารที่สแกน */}
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#e0f2f1' }}>
                                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>รหัสแปลงที่ดินใหม่</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>รหัสแปลงที่ดินเก่า</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>ผู้ถือปัจจุบัน</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>วันที่รับ</TableCell>
                                        <TableCell align="center"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {docData.length > 0 ? (
                                        docData.map((row, index) => (
                                            <TableRow key={index}>
                                                <TableCell align="center">{row.parcelCode || '-'}</TableCell>
                                                <TableCell align="center">{row.oldParcel || '-'}</TableCell>
                                                <TableCell align="center">
                                                    {/* 🟢 เซิร์ฟเวอร์จัดการ fname/lname มาให้เรียบร้อยแล้ว */}
                                                    {`${row.fname || ''} ${row.lname || ''}`.trim() || '-'}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {row.recievedDate ? moment(row.recievedDate).add(543, 'year').format('DD MMMM YYYY เวลา HH:mm:ss') : '-'}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Button
                                                        variant='contained'
                                                        color='error'
                                                        size="small"
                                                        startIcon={<RemoveCircleIcon />}
                                                        onClick={() => {
                                                            const arr = [...docData];
                                                            arr.splice(index, 1);
                                                            setDocData(arr);
                                                            if (inputRef.current) inputRef.current.focus();
                                                        }}
                                                    >
                                                        เอาออก
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                                ยังไม่มีรายการเอกสารในตาราง กรุณาสแกนบาร์โค้ด
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* กลุ่มปุ่มดำเนินการ (Actions) */}
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
                            {/* 1. ปุ่มรับเอกสาร (ลงชื่อผู้สแกนรับ) */}
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SaveIcon />}
                                sx={{ fontSize: '1.1rem', padding: '10px 24px' }}
                                onClick={() => { updateCurrentHolder(docData, setOpenSuccess, setOpenError, setSuccessText, setErrorText) }}
                            >
                                รับเอกสาร
                            </Button>

                            {/* 🟢 2. ปุ่มเสนอผู้บริหาร (เห็นเฉพาะ Admin 100 และ เจ้าหน้าที่ธุรการ 106) */}
                            {canSendToManagement && (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<AssignmentIndIcon />}
                                    sx={{ fontSize: '1.1rem', padding: '10px 24px' }}
                                    onClick={handleSendToManagement}
                                >
                                    เสนอผู้บริหาร
                                </Button>
                            )}

                            {/* 🟢 3. ปุ่มเก็บเข้าแฟ้ม (เฉพาะ Admin 100 และ ห้องซอง 105) */}
                            {canReturnDoc && (
                                <Button
                                    variant="contained"
                                    color="warning"
                                    startIcon={<SaveIcon />}
                                    sx={{ fontSize: '1.1rem', padding: '10px 24px' }}
                                    onClick={() => {
                                        returnDoc(docData, setOpenSuccess, setOpenError, setSuccessText, setErrorText)
                                    }}
                                >
                                    เก็บเข้าแฟ้ม
                                </Button>
                            )}
                        </Box>
                    </CardContent>
                </Card>

                {/* Modal เพิ่มข้อมูลแปลงที่ดินใหม่ */}
                <Modal open={addModalOpen} onClose={handleModalClose}>
                    <Box sx={modalStyle} component="form" onSubmit={handleModalFormSave}>
                        <Typography variant="h6" mb={2} sx={{ fontWeight: 'bold' }}>
                            เพิ่มข้อมูลแปลงที่ดิน
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                label="รหัสแปลงที่ดินใหม่"
                                name="parcelCode"
                                value={modalForm.parcelCode}
                                onChange={handleModalFormChange}
                                error={!!validationErrors.parcelCode}
                                helperText={validationErrors.parcelCode}
                                fullWidth
                                margin="dense"
                            />
                            <TextField
                                label="รหัสแปลงที่ดินเก่า"
                                name="oldParcel"
                                value={modalForm.oldParcel}
                                onChange={handleModalFormChange}
                                error={!!validationErrors.oldParcel}
                                helperText={validationErrors.oldParcel}
                                fullWidth
                                margin="dense"
                            />

                            {/* ตัวเลือกประเภทเอกสารสิทธิ์จาก Constants */}
                            <TextField
                                label="ประเภทเอกสารสิทธิ์"
                                name="parcelType"
                                select
                                value={modalForm.parcelType}
                                onChange={(e) => setModalForm({ ...modalForm, parcelType: e.target.value })}
                                error={!!validationErrors.parcelType}
                                helperText={validationErrors.parcelType}
                                fullWidth
                                margin="dense"
                            >
                                {PARCEL_TYPE_OPTIONS.map((data) => (
                                    <MenuItem key={data.value} value={data.value}>
                                        {data.label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                label="เลขเอกสารสิทธิ์ที่ดิน"
                                name="dn"
                                value={modalForm.dn}
                                onChange={handleModalFormChange}
                                error={!!validationErrors.dn}
                                helperText={validationErrors.dn}
                                fullWidth
                                margin="dense"
                            />
                            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
                                <Button variant="outlined" onClick={handleModalClose}>
                                    ยกเลิก
                                </Button>
                                <Button variant="contained" type='submit'>
                                    บันทึก
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                </Modal>
            </Box>
        </Box>
    )
}